import React, { useRef, useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Upload, FileText, X, Eye, Download, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { taxDocumentsSchema, TaxDocumentsValues } from "@/schemas/sellerProfileSchema";
import { Input } from "@/components/ui/input";
import { uploadFileToBucket } from "@/utils/helper/aws-bucketUpload";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { updateSellerInfo } from "@/graphql/mutations";
import { SellerInfo } from "@/types/AppTypes";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getUserFromLS } from "@/utils/helper/getUserFromLS";
import { fetchSellerInfoQueryOptions } from "@/queryOptions/seller/fetchSellerInfo";

export const TaxDocumentsForm = ({ taxDocuments, distributorInfo }: { taxDocuments: string[], distributorInfo: SellerInfo }) => {
  const user = getUserFromLS();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  const [filePreviews, setFilePreviews] = useState<Array<{ name: string; file: File }>>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  const { data: sellerInfo, refetch } = useQuery(
    fetchSellerInfoQueryOptions({ user_id: { eq: user?.user_id } },{
      enabled: !!user?.user_id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    })  
  );

  const currentTaxDocuments = sellerInfo?.[0]?.tax_documents || [];

  const form = useForm<TaxDocumentsValues>({
    resolver: zodResolver(taxDocumentsSchema),
    defaultValues: {
      taxDocuments: [],
    },
  });

  // Reset form and file previews when currentTaxDocuments changes
  useEffect(() => {
    const hasDocuments = currentTaxDocuments.length > 0;
    setShowUploadForm(!hasDocuments);
    form.reset({ taxDocuments: [] });
    setFilePreviews([]);
    setReplaceMode(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [currentTaxDocuments.length, form]);

  const handleFileChange = (files: FileList | null, onChange: (files: File[]) => void) => {
    if (!files || files.length === 0) return;

    const selectedFiles = Array.from(files);
    const total = currentTaxDocuments.length + filePreviews.length + selectedFiles.length;

    if (total > 5) {
      toast.error("Maximum 5 files allowed.");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg", "image/jpg", "image/png",
    ];

    const invalidFiles = selectedFiles.filter((file) => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error("Invalid file types found.");
      return;
    }

    const previews = selectedFiles.map((file) => ({ name: file.name, file }));
    setFilePreviews((prev) => [...prev, ...previews]);
    onChange(selectedFiles);
  };
  const isPdfFile = (path: string) => {
    return path.toLowerCase().includes('.pdf');
  };
  const isImageFile = (path: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => path.toLowerCase().includes(ext));
  };
  
    const getFileNameFromPath = (path: string) => {
    return path.split('/').pop() || 'Document';
  };

  const handleRemoveFile = (index: number) => {
    setFilePreviews((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      form.setValue("taxDocuments", updated.map((fp) => fp.file));
      return updated;
    });
  };

  const handleRemoveAllFiles = () => {
    setFilePreviews([]);
    form.setValue("taxDocuments", []);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveDocument = async (index: number) => {
    setIsRemoving(true);
    try {
      const updated = [...currentTaxDocuments];
      updated.splice(index, 1);

      await executeGraphQLMutation({
        query: updateSellerInfo,
        variables: {
          input: {
            id: sellerInfo?.[0]?.id,
            tax_documents: updated,
            updated_at: new Date().toISOString(),
          },
        },
      });

      // Invalidate and refetch to ensure UI updates
      await queryClient.invalidateQueries({
        queryKey: ["seller_info", { user_id: user?.user_id }],
      });
      
      // Force refetch to get latest data
      await refetch();

      toast.success("Document removed.");
    } catch {
      toast.error("Failed to remove document.");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveAllDocuments = async () => {
    if (!window.confirm("Are you sure you want to remove all documents?")) return;

    setIsRemoving(true);
    try {
      await executeGraphQLMutation({
        query: updateSellerInfo,
        variables: {
          input: {
            id: sellerInfo?.[0]?.id,
            tax_documents: [],
            updated_at: new Date().toISOString(),
          },
        },
      });

      // Invalidate and refetch to ensure UI updates
      await queryClient.invalidateQueries({
        queryKey: ["seller_info", { user_id: user?.user_id }],
      });
      
      // Force refetch to get latest data
      await refetch();

      toast.success("All documents removed.");
    } catch {
      toast.error("Failed to remove all documents.");
    } finally {
      setIsRemoving(false);
    }
  };

  const onSubmit = async (data: TaxDocumentsValues) => {
    setIsSaving(true);

    let uploadedFiles: string[] = [];
    if (data.taxDocuments?.length && data.taxDocuments[0] instanceof File) {
      uploadedFiles = await Promise.all(
        data.taxDocuments.map((file) => uploadFileToBucket(file, "seller-tax-documents"))
      );
    }

    const finalDocuments =
      replaceMode || currentTaxDocuments.length === 0
        ? uploadedFiles
        : [...currentTaxDocuments, ...uploadedFiles];

    try {
      await executeGraphQLMutation({
        query: updateSellerInfo,
        variables: {
          input: {
            id: sellerInfo?.[0]?.id,
            tax_documents: finalDocuments,
            updated_at: new Date().toISOString(),
          },
        },
      });

      // Invalidate and refetch to ensure UI updates
      await queryClient.invalidateQueries({
        queryKey: ["seller_info", { user_id: user?.user_id }],
      });
      
      // Force refetch to get latest data
      await refetch();

      setReplaceMode(false);
      setFilePreviews([]);
      form.reset();
      setShowUploadForm(false);
      toast.success("Documents uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tax Documents</CardTitle>
        <CardDescription>
          {showUploadForm 
            ? "Upload your W-9 form and other tax-related documents." 
            : "Manage your uploaded tax documents."
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showUploadForm ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">
                  {currentTaxDocuments.length > 0 ? 'Add More Tax Documents' : 'Upload Tax Documents'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {currentTaxDocuments.length > 0 
                    ? 'Add more tax documents to your existing collection.'
                    : 'Upload completed W-9 form and other tax-related documents.'
                  }
                </p>
                
                {currentTaxDocuments.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="replaceMode"
                        checked={replaceMode}
                        onChange={(e) => setReplaceMode(e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="replaceMode" className="text-sm text-blue-800">
                        Replace all existing documents with new ones
                      </label>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      {replaceMode 
                        ? "New documents will replace all existing ones."
                        : "New documents will be added to existing ones."
                      }
                    </p>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="taxDocuments"
                  render={({ field: { onChange, value, ...field } }) => (
                    <FormItem>
                      <FormLabel>Tax Documents</FormLabel>
                      <FormControl>
                        <div className="relative border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-gray-300 transition-colors cursor-pointer">
                          {filePreviews.length > 0 ? (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                                              <h4 className="text-sm font-medium text-gray-900">
                                Uploaded Files ({currentTaxDocuments.length + filePreviews.length}/5)
                              </h4>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleRemoveAllFiles}
                                  className="text-red-500 hover:text-red-700 relative z-10"
                                >
                                  Remove All
                                </Button>
                              </div>
                              <div className="space-y-2">
                                {filePreviews.map((filePreview, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg relative z-10"
                                  >
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-5 w-5 text-blue-500" />
                                      <span className="text-sm font-medium">{filePreview.name}</span>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                        onClick={() => handleRemoveFile(index)}
                                      className="text-red-500 hover:text-red-700 relative z-10"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                              <div className="pt-4 border-t border-gray-200">
                                <p className="text-sm text-gray-500">Click to add more files</p>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <p className="text-lg font-medium text-gray-900 mb-2">
                                Click to upload
                              </p>
                              <p className="text-sm text-gray-500 mb-4">
                                PDF, DOC, DOCX, JPG, JPEG, or PNG (max. 5 files, 5MB per file)
                              </p>
                            </div>
                          )}
                          <Input
                            {...field}
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            multiple
                            onChange={(e) => handleFileChange(e.target.files, onChange)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-0"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                {currentTaxDocuments.length > 0 && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowUploadForm(false)}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Documents
                  </Button>
                )}
                <Button type="submit" className="w-full md:w-auto" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Documents"}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Uploaded Tax Documents</h3>
              <div className="flex gap-2">
                {currentTaxDocuments.length < 5 && (
                  <Button
                    variant="outline"
                    onClick={() => setShowUploadForm(true)}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Add More
                  </Button>
                )}
                {currentTaxDocuments.length > 1 && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveAllDocuments}
                    disabled={isRemoving}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                    Remove All
                  </Button>
                )}
              </div>
            </div>

            {currentTaxDocuments.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No tax documents uploaded yet.</p>
                <Button
                  variant="outline"
                  onClick={() => setShowUploadForm(true)}
                  className="mt-4"
                >
                  Upload Documents
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {currentTaxDocuments.map((docPath, index) => (
                  <div
                    key={index}
                    className="flex flex-col h-full border rounded-lg p-4 bg-white shadow-sm"
                  >
                    {/* Top: Filename and Remove Button */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                        <span className="font-medium truncate text-sm text-gray-800 max-w-[180px]" title={getFileNameFromPath(docPath)}>
                          {getFileNameFromPath(docPath)}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveDocument(index)}
                        disabled={isRemoving}
                        className="text-red-600 hover:text-red-700"
                        aria-label="Remove document"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Middle: Preview */}
                    <div className="flex-1 flex items-center justify-center my-2">
                      {isImageFile(docPath) ? (
                        <img
                          src={docPath}
                          alt={`Tax Document ${index + 1}`}
                          className="max-w-full max-h-40 object-contain rounded border"
                        />
                      ) : isPdfFile(docPath) ? (
                        <div className="flex flex-col items-center justify-center w-full">
                          <FileText className="h-12 w-12 text-red-500 mb-2" />
                          <span className="text-xs text-gray-500">PDF Document</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center w-full">
                          <FileText className="h-12 w-12 text-gray-400 mb-2" />
                          <span className="text-xs text-gray-500">Document</span>
                        </div>
                      )}
                    </div>

                    {/* Bottom: Action Buttons */}
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1"
                      >
                        <a
                          href={docPath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 justify-center"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
